package dev.danko.roblox_craft_ai.uikit.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import dev.danko.roblox_craft_ai.shared.model.AssetResponse
import dev.danko.roblox_craft_ai.uikit.icons.AppIcon
import dev.danko.roblox_craft_ai.uikit.i18n.strings
import dev.danko.roblox_craft_ai.uikit.theme.LocalAppTheme
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.TextArea

/** Change a finished animation in words; every change is a version that can be restored. */
@Composable
fun AnimationRefinePanel(asset: AssetResponse, tools: MeshToolsActions) {
    val a = strings().animationSetup
    val palette = LocalAppTheme.current.colors
    val scope = rememberCoroutineScope()
    var text by remember(asset.id) { mutableStateOf("") }
    var busy by remember(asset.id) { mutableStateOf(false) }

    fun run(block: suspend () -> Unit) {
        if (busy) return
        busy = true
        scope.launch {
            try {
                block()
            } catch (error: Throwable) {
                ToastManager.error(error.message?.takeIf { it.isNotBlank() } ?: a.editFailed, title = a.editFailed)
            } finally {
                busy = false
            }
        }
    }

    Column(
        Modifier.fillMaxWidth().gap(10.px).padding(14.px).borderRadius(12.px)
            .backgroundColor(palette.background).border(1.px, LineStyle.Solid, palette.border)
    ) {
        SpanText(a.editTitle, Modifier.fontSize(14.px).fontWeight(FontWeight.SemiBold).color(palette.textPrimary))
        SpanText(a.editHint, Modifier.fontSize(12.px).color(palette.textSecondary))
        Row(Modifier.fillMaxWidth().gap(6.px).flexWrap(FlexWrap.Wrap)) {
            a.editPresets.forEach { preset ->
                AppButton(text = preset, variant = AppButtonVariant.OUTLINE, size = AppButtonSize.SM, enabled = !busy, onClick = { text = preset })
            }
        }
        TextArea(
            attrs = Modifier.fillMaxWidth().padding(10.px).borderRadius(9.px)
                .border(1.px, LineStyle.Solid, palette.borderStrong).backgroundColor(palette.surface).color(palette.textPrimary)
                .styleModifier { property("font-family", "inherit"); property("resize", "vertical") }
                .toAttrs {
                    attr("rows", "2")
                    attr("maxlength", "400")
                    attr("placeholder", a.editPlaceholder)
                    attr("aria-label", a.editTitle)
                    value(text)
                    onInput { event -> text = event.value }
                }
        )
        Row(Modifier.fillMaxWidth().gap(8.px), verticalAlignment = Alignment.CenterVertically) {
            AppButton(
                text = if (busy) a.editWorking else a.editApply(tools.animationEditCost()),
                icon = AppIcon.SPARKLES,
                size = AppButtonSize.SM,
                loading = busy,
                enabled = !busy && text.isNotBlank(),
                onClick = {
                    val instruction = text.trim()
                    run {
                        val updated = tools.editAnimation(asset.id, instruction)
                        text = ""
                        ToastManager.success(a.editDone(updated.metadata["currentRevision"]?.toIntOrNull() ?: 2))
                    }
                }
            )
        }

        val versions = asset.revisions.filter { it.metadata["keyframesJson"] != null }.sortedByDescending { it.revisionNumber }
        if (versions.size > 1) {
            SpanText(a.versionsTitle, Modifier.fontSize(12.px).fontWeight(FontWeight.SemiBold).color(palette.textSecondary).margin(top = 4.px))
            val current = asset.metadata["currentRevision"]?.toIntOrNull() ?: versions.first().revisionNumber
            versions.forEach { version ->
                Row(Modifier.fillMaxWidth().gap(8.px), verticalAlignment = Alignment.CenterVertically) {
                    SpanText(
                        "${version.revisionNumber}. " + if (version.revisionNumber == 1) a.versionOriginal else "«${version.prompt}»",
                        Modifier.fontSize(13.px).color(if (version.revisionNumber == current) palette.textPrimary else palette.textSecondary)
                            .fontWeight(if (version.revisionNumber == current) FontWeight.SemiBold else FontWeight.Normal).flexGrow(1)
                    )
                    if (version.revisionNumber != current) {
                        AppButton(text = a.restore, variant = AppButtonVariant.OUTLINE, size = AppButtonSize.SM, enabled = !busy, onClick = {
                            run { tools.restoreAnimation(asset.id, version.revisionNumber) }
                        })
                    }
                }
            }
        }
    }
}
